/**
 * EntityStore: Global cache for entity METADATA only
 * 
 * 🛡️ CRITICAL DATA LOSS PREVENTION:
 * - Doc entities: Does NOT cache content (skeleton.base64/content)
 * - Always fetch fresh content when opening documents via entityService.get(id)
 * - Caching only metadata: name, id, llmId, timestamps, icon
 * 
 * WHY: EntityStore uses /api/entities (list endpoint) which strips base64 for performance.
 * If we cached this stripped content, documents would load empty when opened from cache.
 * Solution: Strip base64/content from cache, force fresh fetch when opening documents.
 */
import debounce from 'lodash/debounce';
import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';

// entityCRDTService removed: entity sync now flows through entity-system Y.Doc
// (gui/src/stores/entitySystemYjsStore.ts subscribeToEntitySystemChanges)
import type { SpaceUpdate } from '../services/crdt/spaceCRDTService';
import { entityService } from '../services/entityService';
import { fileService } from '../services/FileService';
import { BaseEntityType } from '../types';
import { entityCacheService } from '../services/entityCacheService';
import { getCurrentUserId } from '../utils/userIdUtils';

import { createFileLogger } from '../utils/logger';
import { createEntityActions } from './entityActions';

// ✅ PHASE 2: Memoization caches to prevent infinite re-render loops
let cachedDisplayableEntities: BaseEntityType[] = [];
let lastEntitiesRef: BaseEntityType[] | null = null;

let cachedTrashedEntities: BaseEntityType[] = [];
let lastTrashedRef: BaseEntityType[] | null = null;

// ✅ BUG FIX #2: Stable timestamps for synthetic system groups
// Using dynamic timestamps (new Date()) causes unnecessary re-renders
const STABLE_SYSTEM_TIMESTAMP = new Date('2026-01-01T00:00:00Z').toISOString();

// ⚡ PERFORMANCE FIX: Use Set for O(1) lookups instead of O(n) array.includes()
// For getDisplayableEntities and getTrashedEntities filters
// Changes 8 comparisons → 1 hash lookup (8x faster)
const HIDDEN_ENTITY_TYPES = new Set([
  'DocVersion',
  'Message',
  'CommentThread',
  'Thread',
  'Space',
  'Window',
  'SearchResults',
  'ManuscriptDiscoveryJob',
  'APIConnection',
  'Integration',      // Hide integration state entities from sidebar
  // ✅ Integration data entities: system-managed records from external services.
  // These are queryable via river.entity.query() in apps but are NOT user-organizable
  // documents — they should never appear in space sidebars.
  'BankTransaction',
  'PlaidItem',       // Legacy — superseded by Integration.Plaid
  'PlaidTransaction', // Legacy — superseded by BankTransaction
  'PlaidAccount',    // Legacy — superseded by BankAccount
  'GoogleDriveFile',
  'Contact',         // Managed by ContactsApp — not user-organizable documents
  'Mail.Contact',    // Mail contacts — managed by ContactsApp
  'RiverAppStorage',  // App storage buckets are internal — never shown to users
  'Task',             // Managed by TasksApp — never shown in file tree
  'Project',          // Managed by TasksApp — never shown in file tree
  'Area',             // Managed by TasksApp — never shown in file tree
  'Job',              // Automation run records — never shown in file tree
  'Social.Tweet',     // Imported tweets — searchable but not in file tree
  // 'Automation' intentionally NOT hidden — shows in sidebar tree via dataspace edge
  // 'Rule' intentionally NOT hidden — space-scoped Rule entities (Operating Manual) appear
  // in the sidebar tree under their parent space, like .cursor/ files in a project.
  // Rules without spaceId have no dataspace_edge and won't appear in any tree.
  'Schedule',         // Automation schedules — never shown in file tree
  'Terminal',         // AI-created persistent terminal sessions — never shown in file tree
  'Mail.Message',     // Thread contents — fetched on demand by ThreadDetail via /messages endpoint, never loaded into global store (140k+ per user would destroy performance)
  'Mail.Thread',      // Owned by mailAppStore._threadCache — fetched via typed REST, not included in default bulk load
  'Mail.Folder',      // Owned by mailAppStore — fetched on demand
  'Mail.Draft',       // Owned by mailAppStore — fetched on demand
  'Messaging.Message', // Chat messages — fetched on demand by ConversationDetail, same pattern as Mail.Message
  'System.Memory',       // Intelligence layer — managed by consolidation pipeline, not user-organizable
  'System.UserProfile',  // Intelligence layer — derived per-user profile, managed internally
]);

interface EntityEvent {
  id: string;
  timestamp: number;
  userId: string;
  action: 'create' | 'update' | 'delete';
  entityId: string;
  data?: BaseEntityType;
  previousData?: BaseEntityType;
}

export interface EntityState {
  entities: BaseEntityType[];
  authToken: string | null;
  status: 'idle' | 'loading' | 'error';
  error?: Error;
  events: EntityEvent[];
  undoStack: EntityEvent[];
  redoStack: EntityEvent[];
  initialized: boolean;
  isObserving: boolean;  // ✅ Track if CRDT observers are active
  dataspaceRoomKey?: string;
  initializationPromise?: Promise<void> | null;  // ✅ Track in-flight initialization
  initializationEpoch: number;  // Incremented on each new initialize() call; guards stale writes
  lifecycle: 'uninitialized' | 'loading' | 'ready' | 'error';  // ✅ NEW: Formal lifecycle state
  currentDataspaceKey?: string | null;  // ✅ ORGS V1: Track current dataspace (personal or orgId)
  dataspaceEntities: Record<string, BaseEntityType[]>;  // ✅ ORGS V1: Cache entities per dataspace
  
  // ✅ Computed state: Connected integrations (O(1) lookup for tool filtering)
  // Updated automatically in _applyLocalUpsert mutation funnel
  connectedIntegrations: Set<string>;  // e.g., Set(['Gmail', 'GoogleCalendar', 'GoogleDrive'])
}

export interface EntityStore extends EntityState {
  setAuthToken: (token: string | null) => void;
  addEntity: (entity: BaseEntityType, parentId?: string) => void;
  deleteEntity: (id: string) => void;
  initialize: (dataspaceRoomKey: string) => Promise<void>;
  performDelete: (id: string) => Promise<void>;
  updateEntity: (entity: BaseEntityType) => void;
  setEntities: (entities: BaseEntityType[]) => void;
  fetchEntities: () => Promise<void>;
  undo: () => void;
  redo: () => void;
  logEvent: (event: Omit<EntityEvent, 'id' | 'timestamp'>) => void;
  cleanup: () => void;
  cleanupFn?: () => void;
  removeEntity: (entityId: string) => void;
  
  // Internal helpers
  _updateConnectedIntegrations: (entities: BaseEntityType[]) => Set<string>;
  onSpaceUpdate: (spaceId: string, update: SpaceUpdate) => void;
  
  // Entity lookup helpers
  getEntityById: (id: string) => BaseEntityType | undefined;
  getEntityByLlmId: (id: string) => BaseEntityType | undefined;
  
  // ✅ PHASE 2: Entity filtering selectors (replaces HierarchyStore)
  getDisplayableEntities: () => BaseEntityType[];
  getTrashedEntities: () => BaseEntityType[];
  getSystemGroups: () => {
    spaceChatGroup: BaseEntityType | null;
  };
  
  // Internal mutation helpers (single code path for all mutations)
  _applyLocalUpsert: (entity: BaseEntityType, opts?: {
    log?: boolean;
    source?: 'local' | 'crdt' | 'event';
    syncCRDT?: boolean;
  }) => BaseEntityType;
  _applyLocalDelete: (id: string, opts?: {
    log?: boolean;
    source?: 'local' | 'crdt' | 'event';
    syncCRDT?: boolean;
  }) => void;
  
  // Entity actions (canonical mutation API)
  actions: {
    createFolder: (name: string, parentId?: string, options?: { color?: string, spaceId?: string, edge_pos?: string }) => Promise<BaseEntityType>;
    moveEntity: (entityId: string, targetParentId: string | null) => Promise<void>;
    createEntity: (data: Partial<BaseEntityType>) => Promise<BaseEntityType>;
    updateEntity: (entityId: string, data: Partial<BaseEntityType>) => Promise<BaseEntityType>;
    getEntity: (entityId: string) => Promise<BaseEntityType>;
    softDeleteEntity: (entityId: string) => Promise<void>;
    closeWindowEntity: (entityId: string) => Promise<void>;
    undeleteEntity: (entityId: string) => Promise<void>;
    groupEntities: (entityIds: string[], folderName: string, parentId?: string) => Promise<any>;
    ungroupEntities: (folderId: string) => Promise<void>;
  };
}

/**
 * Normalize entity for cache storage
 * - Strips base64/content from Doc entities (data loss prevention)
 * - Ensures required fields
 */
function normalizeEntityForCache(e: BaseEntityType): BaseEntityType {
  // Start with all fields from the entity, then override specific ones
  const baseEntity: BaseEntityType = {
    ...e,
    created_at: e.created_at || new Date().toISOString(),
    updated_at: e.updated_at || new Date().toISOString(),
    type: e.type || e.entityType,
    entityType: e.entityType || e.type,
  };

  // 🛡️ DATA LOSS PREVENTION: Never cache document content
  if (e.entityType === 'Doc' && e.skeleton) {
    return {
      ...baseEntity,
      skeleton: {
        ...e.skeleton,
        base64: undefined,          // ❌ Strip content (data loss prevention)
        content: undefined,         // ❌ Strip content (data loss prevention)
        // ✅ KEEP metadata (nodeCount, wordCount, lastSaved)
        // This ensures fresh metadata flows through when entities are updated
      },
      // ✅ ROBUSTNESS: Mark as cached so we can prevent accidental saves
      __fromCache: true,
    } as any;
  }

  return baseEntity;
}

/**
 * Flatten hierarchical entity tree to flat array
 * - Strips children property (buildEntityHierarchy will rebuild it)
 * - Used for merging and comparing entities
 */
function flattenEntities(entities: BaseEntityType[]): BaseEntityType[] {
  const result: BaseEntityType[] = [];
  const traverse = (entity: BaseEntityType) => {
    // Strip children property before adding (buildEntityHierarchy will rebuild it)
    const { children, ...entityWithoutChildren } = entity as any;
    result.push(entityWithoutChildren);
    if (children && Array.isArray(children)) {
      children.forEach(traverse);
    }
  };
  entities.forEach(traverse);
  return result;
}

const useEntityStore = create<EntityStore>((set, get) => {
  const log = createFileLogger('ENTITY_STORE', { enabled: false });
  
  // ✅ REMOVED: Manual cache update function
  // Cache sync is now handled automatically via Zustand subscription (see bottom of file)
  // This ensures 100% coverage of all entity mutations without manual intervention
  
  // ═══════════════════════════════════════════════════════════════════════════
  // 🔍 ENTITY FILTERING SELECTORS (Phase 2 Cleanup - Replaces HierarchyStore)
  // ═══════════════════════════════════════════════════════════════════════════
  
  // ✅ Export constants for use in other files
  const SYSTEM_ENTITY_IDS = {
    SPACECHAT: '__spacechat__',
    INTEGRATIONS: '__integrations__',
  } as const;
  
  /**
   * Internal helper: Apply local entity upsert (create or update)
   * This is the SINGLE code path for all entity mutations
   */
  const _applyLocalUpsert = (
    entity: BaseEntityType,
    opts?: {
      log?: boolean;
      source?: 'local' | 'crdt' | 'event';
      syncCRDT?: boolean;
    }
  ): BaseEntityType => {
    const options = {
      log: true,
      source: 'local' as const,
      syncCRDT: true,
      ...opts
    };
    
    // ✅ RUNTIME GUARDS: Prevent critical bugs
    
    // 🔴 CRITICAL GUARD: Entity MUST have an _id
    if (!entity._id && !(entity as any).id) {
      console.error('[entityStore] 🔴 CRITICAL: Entity missing _id!', {
        entityName: entity.name,
        entityType: entity.entityType,
        allKeys: Object.keys(entity),
      });
      throw new Error(`[entityStore] Entity missing _id: ${entity.name} (${entity.entityType})`);
    }
    
    // Guard: base64 should not be in cache (prevents stale content)
    if (entity.entityType === 'Doc' && (entity.skeleton as any)?.base64) {
      console.error('[entityStore] ⚠️ RUNTIME GUARD: base64 in cache - should be stripped', {
        entityId: entity._id,
        name: entity.name,
        hasBase64: !!(entity.skeleton as any)?.base64
      });
    }
    
    // 🔴 CRITICAL GUARD: Prevent CRDT infinite loop (browser crash)
    // CRDT updates MUST NOT sync back to CRDT (creates infinite loop)
    if (options.source === 'crdt' && options.syncCRDT !== false) {
      const errorMsg = `[entityStore] 🔄 CRDT LOOP PREVENTED! CRDT updates must set syncCRDT=false. Entity: ${entity._id}`;
      console.error(errorMsg, {
        entityId: entity._id,
        source: options.source,
        syncCRDT: options.syncCRDT,
        stack: new Error().stack
      });
      // THROW to prevent infinite loop - this is critical
      throw new Error(errorMsg);
    }
    
    const normalized = normalizeEntityForCache(entity);
    const currentState = get();
    
    // Only log if it's a local action (not from CRDT/event)
    if (options.log && options.source === 'local') {
      const prev = currentState.entities.find((e) => e._id === normalized._id);
      get().logEvent({
        userId: 'current-user',
        action: prev ? 'update' : 'create',
        entityId: normalized._id,
        data: normalized,
        previousData: prev,
      });
    }
    
    // No explicit CRDT write needed here: the REST handler (backend/routes/entities.py)
    // already broadcasts to entity-system Y.Doc via POST /crdt/entity-system/.../update,
    // which syncs to all connected clients automatically via WebSocket.
    // (Previously used entityCRDTService.createEntity/updateEntity — removed to eliminate
    //  the sync feedback loop with entities:org:... room.)
    
    // Always update local state
    set((state) => {
      const existingIndex = state.entities.findIndex((e) => e._id === normalized._id);
      let newEntities: BaseEntityType[];
      
      if (existingIndex >= 0) {
        newEntities = [...state.entities];
        newEntities[existingIndex] = normalized;
        // log.log('🔄 [ENTITY_STORE] Entity updated:', {
        //   entityId: normalized._id,
        //   entityName: normalized.name,
        //   entityType: normalized.entityType,
        //   source: options.source,
        //   hasSpaceId: !!normalized.spaceId,
        //   spaceId: normalized.spaceId,
        //   groupIds: (normalized.skeleton as any)?.groupIds,
        //   childEntities: (normalized.skeleton as any)?.childEntities
        // });
      } else {
        newEntities = [...state.entities, normalized];
        // log.log('➕ [ENTITY_STORE] Entity added:', {
        //   entityId: normalized._id,
        //   entityName: normalized.name,
        //   entityType: normalized.entityType,
        //   source: options.source,
        //   groupIds: (normalized.skeleton as any)?.groupIds,
        //   childEntities: (normalized.skeleton as any)?.childEntities,
        //   totalEntities: newEntities.length
        // });
      }
      
      // ✅ Update computed state: connectedIntegrations
      const updatedConnectedIntegrations = get()._updateConnectedIntegrations(newEntities);
      
      return { 
        entities: buildEntityHierarchy(newEntities),
        connectedIntegrations: updatedConnectedIntegrations
      };
    });
    
    // ✅ IndexedDB sync handled automatically by subscription (see bottom of file)
    
    return normalized;
  };
  
  /**
   * Internal helper: Apply local entity delete
   * This is the SINGLE code path for all entity deletions
   */
  const _applyLocalDelete = (
    id: string,
    opts?: {
      log?: boolean;
      source?: 'local' | 'crdt' | 'event';
      syncCRDT?: boolean;
    }
  ): void => {
    const options = {
      log: true,
      source: 'local' as const,
      syncCRDT: true,
      ...opts
    };
    
    const currentState = get();
    const prev = currentState.entities.find((e) => e._id === id);
    
    // Only log if it's a local action (not from CRDT/event)
    if (options.log && options.source === 'local' && prev) {
      get().logEvent({
        userId: 'current-user',
        action: 'delete',
        entityId: id,
        previousData: prev,
      });
    }
    
    // Deletion is broadcast by the REST DELETE handler (backend/routes/entities.py)
    // via entity-system Y.Doc; no local CRDT write needed.
    
    // Always update local state
    set((state) => {
      const newEntities = state.entities.filter((e) => e._id !== id);
      
      // ✅ Update computed state: connectedIntegrations
      const updatedConnectedIntegrations = get()._updateConnectedIntegrations(newEntities);
      
      return {
        entities: newEntities,
        connectedIntegrations: updatedConnectedIntegrations
      };
    });

    // Delete the entity's y-indexeddb database (fire-and-forget, never blocks UI).
    // _applyLocalDelete is only called for permanent deletes; soft-deletes go through
    // _applyLocalUpsert with trashed:true and are not handled here.
    import('../services/sync/localFirstSyncService').then(({ localFirstSyncService }) => {
      localFirstSyncService.deleteEntityDoc(id)
    }).catch(() => {})

    // ✅ IndexedDB sync handled automatically by subscription (see bottom of file)
  };
  

  /**
   * Helper: Merge fresh entities from backend into current entity array.
   * Defined as a variable so it can be referenced from checkAndSyncStaleEntities.
   */
  const mergeEntities = (
    currentEntities: BaseEntityType[],
    freshEntities: BaseEntityType[]
  ): BaseEntityType[] => {
    const flatCurrent = flattenEntities(currentEntities);
    
    log.info('[EntityStore] Flattened current entities', {
      hierarchicalCount: currentEntities.length,
      flatCount: flatCurrent.length
    });
    
    // Create lookup map of fresh entities by llmId
    const freshMap = new Map<string, BaseEntityType>();
    for (const entity of freshEntities) {
      if (entity.llmId) {
        freshMap.set(entity.llmId, entity);
      }
    }
    
    // ✅ SIMPLIFIED: Just overwrite local with backend data (backend is source of truth)
    const mergedEntities = flatCurrent.map(entity => {
      if (entity.llmId && freshMap.has(entity.llmId)) {
        const fresh = freshMap.get(entity.llmId)!;
        log.info('[EntityStore] Overwriting local entity with backend version', {
          llmId: entity.llmId,
          oldName: entity.name,
          newName: fresh.name,
          oldUpdatedAt: entity.updated_at,
          newUpdatedAt: fresh.updated_at
        });
        freshMap.delete(entity.llmId); // Mark as processed
        return fresh;  // ✅ Always use backend version
      }
      return entity;
    });
    
    // Add any remaining fresh entities that weren't in current (new entities)
    for (const [llmId, freshEntity] of freshMap.entries()) {
      log.info('[EntityStore] Adding new entity from backend', {
        llmId,
        name: freshEntity.name
      });
      mergedEntities.push(freshEntity);
    }
    
    log.info('[EntityStore] Merge complete', {
      originalCount: flatCurrent.length,
      freshCount: freshEntities.length,
      mergedCount: mergedEntities.length
    });
    
    return mergedEntities;
  };
  

  return {
  entities: [],
  authToken: localStorage.getItem('authToken'),
  status: 'idle',
  events: [],
  undoStack: [],
  redoStack: [],
  initialized: false,
  isObserving: false,  // ✅ OPTIMIZATION: Track if CRDT observer is active
  dataspaceRoomKey: undefined,
  initializationPromise: null,
  initializationEpoch: 0,
  lifecycle: 'uninitialized',  // ✅ NEW: Initial lifecycle state

  // ✅ ORGS V1: Separate entity state per dataspace
  dataspaceEntities: {
    personal: []
  },
  currentDataspaceKey: 'personal',
  
  // ✅ Computed state: Connected integrations (O(1) lookup)
  connectedIntegrations: new Set(),
  
  // Expose internal helpers
  _applyLocalUpsert,
  _applyLocalDelete,
  
  // ✅ Helper: Recompute connected integrations Set (O(n) but only on mutations)
  _updateConnectedIntegrations: (entities: BaseEntityType[]) => {
    const connected = entities
      .filter((e: any) => 
        (e.entityType === 'Integration' || e.entityType?.startsWith('Integration.')) &&
        e.skeleton?.status === 'connected'
      )
      .map((e: any) => e.skeleton?.integrationType)
      .filter(Boolean);
    
    return new Set(connected);
  },
  
  // Initialize entity actions (canonical mutation API)
  actions: createEntityActions(set, get),

  initialize: async (dataspaceRoomKey: string) => {
    const currentState = get();
    
    log.info('[EntityStore] 🚀 initialize() called', { dataspaceRoomKey, wasInitialized: currentState.initialized, hasPromise: !!currentState.initializationPromise });

    // ✅ CRITICAL FIX: Prevent duplicate initialization by checking in-flight promise
    // But only reuse the promise if it's for the SAME room key
    if (currentState.initializationPromise && currentState.dataspaceRoomKey === dataspaceRoomKey) {
      log.info('[EntityStore] ⚡ Initialization already in progress for same room key, waiting for existing promise');
      return currentState.initializationPromise;
    }
    
    // If there's a promise for a DIFFERENT room key, clear it and start fresh
    if (currentState.initializationPromise && currentState.dataspaceRoomKey !== dataspaceRoomKey) {
      log.info('[EntityStore] 🔄 Clearing initialization promise for different room key (was:', currentState.dataspaceRoomKey, 'now:', dataspaceRoomKey, ')');
      set({ initializationPromise: null, initialized: false, lifecycle: 'uninitialized' });
    }

    // If already initialized and the lifecycle is ready, skip re-init entirely.
    // entityStore is dataspace-scoped (loads ALL user entities), not space-scoped.
    // Once 'ready', the entity cache is valid for every space in the current dataspace.
    // Callers may pass different ID formats (crdtRoomId vs spaceId); that difference must
    // not cause a redundant full entity fetch.
    // Dataspace changes are handled upstream (App.tsx resets initialized=false before
    // calling initialize() again), so this guard cannot hide a stale-dataspace scenario.
    if (currentState.initialized && currentState.lifecycle === 'ready') {
      log.info('[EntityStore] Already initialized and ready — skipping re-init');
      return;
    }

    // Clean up previous if needed
    if (currentState.cleanupFn) {
      currentState.cleanupFn();
    }

    // Monotonically increasing epoch — each initialize() gets a unique number.
    // Stale inits compare their captured epoch to the current store epoch.
    const epoch = currentState.initializationEpoch + 1;
    set({ initializationEpoch: epoch });

    const initForRoomKey = dataspaceRoomKey;

    // Set dataspaceRoomKey immediately so concurrent callers see the correct key
    // during the entire async initialization (not just when it completes). Without
    // this, the "different room key" guard fires on any concurrent caller while
    // dataspaceRoomKey is still undefined, clearing the in-flight promise and
    // triggering a redundant full entity fetch.
    set({ dataspaceRoomKey });

    const isStillCurrent = () => {
      const s = get();
      return s.initializationEpoch === epoch;
    };

    // Create and store the initialization promise
    const initPromise = (async () => {
      try {
        set({ status: 'loading', lifecycle: 'loading' });
        const perfStart = performance.now();
        log.info('[EntityStore] Fetching ALL user entities (not space-filtered)');
      
      // Check if we have authentication before trying to fetch
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        log.warn('[EntityStore] No auth token available, deferring initialization');
        set({ status: 'idle', lifecycle: 'uninitialized' });
        return;
      }
      
      // ✅ PERFORMANCE BOOST: Try IndexedDB cache first
      // Extract userId from JWT token (more reliable than localStorage)
      const userId = getCurrentUserId();
      
      // Early return if no valid user ID (not authenticated properly)
      if (!userId) {
        log.warn('[EntityStore] No valid userId available, fetching without cache');
        const fetchStart = performance.now();
        const fetchedEntities = await entityService.getAllUserEntities();
        const fetchTime = performance.now() - fetchStart;
        log.info(`⏱️ [PERF] Entity API fetch (no cache): ${fetchTime.toFixed(2)}ms | ${fetchedEntities.length} entities`);
        
        // Continue with normal flow (no caching) - jump to entity processing
        const entities = fetchedEntities.map(normalizeEntityForCache);
        
        // Initialize CRDT and set entities (same as cached path)
        const currentObservingState = get();
        if (!isStillCurrent()) {
          log.warn('[EntityStore] Space changed during init (no-userId path, observer check) — discarding');
          return;
        }
        if (currentObservingState.isObserving) {
          log.warn('[EntityStore] CRDT observer already active, skipping duplicate setup');
          if (!isStillCurrent()) {
            log.warn('[EntityStore] Space changed during init (no-userId path), discarding stale result');
            return;
          }
          set({ 
            entities: buildEntityHierarchy(entities), 
            status: 'idle',
            lifecycle: 'ready',
            initialized: true,
            dataspaceRoomKey: initForRoomKey
          });
          return;
        }
        
        // Entity-system Y.Doc pushes updates to entityStore directly via its own
        // entitiesObserver in entitySystemYjsStore.ts (active after WS sync).
        // No separate subscription needed here — entitySystemYjsStore handles it.
        const cleanupCRDT = () => {};

        // Bail if a newer initialize() call has taken over for a different space
        if (!isStillCurrent()) {
          log.warn('[EntityStore] Space changed during init (no-userId path), discarding stale result');
          cleanupCRDT();
          return;
        }


        // ✅ Update computed state
        const connectedIntegrations = get()._updateConnectedIntegrations(entities);
        
        set({
          entities: buildEntityHierarchy(entities),
          connectedIntegrations,
          status: 'idle',
          lifecycle: 'ready',
          isObserving: true,
          cleanupFn: () => {
            cleanupCRDT();
            set({ isObserving: false });
          },
          dataspaceRoomKey: initForRoomKey,
          initialized: true,
        });
        
        const totalTime = performance.now() - perfStart;
        log.info(`📊 [PERF SUMMARY] No cache (no userId) - Total time: ${totalTime.toFixed(2)}ms`);
        return;
      }
      
      // ── Fetch entities from REST (authoritative source) ──────────────
      // Entity loading is always REST-based. The org Y.Doc only carries lightweight
      // metadata (name, icon, entityType) for real-time sync; it is not a full
      // entity cache.
      const fetchStart = performance.now();
      const fetchedEntities = await entityService.getAllUserEntities();
      const fetchTime = performance.now() - fetchStart;
      log.info(`⏱️ [PERF] Entity API fetch: ${fetchTime.toFixed(2)}ms | ${fetchedEntities.length} entities`);

      // 🛡️ CRITICAL: Backend already strips large data, frontend adds extra safety
      // ========================================================================
      // BACKEND STRIPPING (in /api/entities):
      // - Message entities: Excluded entirely (fetch per-thread)
      // - Doc entities: base64 field stripped
      // - MediaGen.Asset.Image: skeleton.url (base64) stripped, replaced with null
      // - Space entities: windows.{id}.entity (nested docs) stripped
      // ========================================================================
      // FRONTEND ADDITIONAL STRIPPING (for cache safety):
      // - Doc entities: Strip base64 + content (prevents stale content bugs)
      // ========================================================================
      const entities = fetchedEntities.map(normalizeEntityForCache);

      // ✅ User-scoped rooms (e.g., "user:auth0|...") are valid CRDT rooms
      // They provide real-time sync scoped to a single user across all their spaces

      // ✅ OPTIMIZATION: Prevent duplicate CRDT observers
      const currentObservingState = get();
      if (!isStillCurrent()) {
        log.warn('[EntityStore] Space changed during init (main path, observer check) — discarding REST results');
        return;
      }
      if (currentObservingState.isObserving) {
        log.warn('[EntityStore] CRDT observer already active, skipping duplicate setup');
        if (!isStillCurrent()) {
          log.warn('[EntityStore] Space changed during init, discarding stale result');
          return;
        }
        set({ 
          entities: buildEntityHierarchy(entities), 
          status: 'idle',
          lifecycle: 'ready',
          initialized: true,
          dataspaceRoomKey: initForRoomKey
        });
        return;
      }

      // Entity-system Y.Doc pushes updates to entityStore directly via its own
      // entitiesObserver in entitySystemYjsStore.ts (active after WS sync).
      // No separate subscription needed here.
      const cleanupCRDT = () => {};

      // ✅ NEW: Set up entity event listener for cross-service synchronization
      // This catches entity changes from FileService, WordImport, etc. that bypass CRDT
      let cleanupEntityEvents: (() => void) | null = null;
      try {
        const { entityEventService } = await import('../services/entityEventService');
        log.info('[EntityStore] ✅ Setting up entity event listener (listening to ALL spaces)');
        
        cleanupEntityEvents = entityEventService.onEntityEvent('*', (event) => {
          // ✅ CRITICAL FIX: Don't filter by spaceId
          // EntityStore manages ALL user entities across ALL spaces,
          // so we need to process events from any space the user has access to
          
          // ✅ ARCHITECTURE FIX: Ignore 'local' events - they were already processed by the action
          // that called _applyLocalUpsert. Only process 'remote' or 'crdt' events.
          if (event.source === 'local') {
            log.log('[EntityStore] 📨 Ignoring local event (already processed by action):', {
              type: event.type,
              entityId: event.entity._id,
              entityName: event.entity.name
            });
            return;
          }
          
          log.log(`[EntityStore] 📨 Entity event received: type=${event.type} entityId=${event.entity._id} name="${event.entity.name}" entityType=${event.entity.entityType} source=${event.source}`);

          if (event.type === 'created' || event.type === 'updated') {
            // Use helper for event updates (no logging, no CRDT sync - caller already did it)
            get()._applyLocalUpsert(event.entity, {
              source: 'event',
              log: false,
              syncCRDT: false
            });
            log.log('[EntityStore] ✅ Synced entity from event:', event.entity._id);
          } else if (event.type === 'deleted') {
            // Use helper for event deletions (no logging, no CRDT sync)
            get()._applyLocalDelete(event.entity._id, {
              source: 'event',
              log: false,
              syncCRDT: false
            });
            log.log('[EntityStore] ✅ Removed entity from event:', event.entity._id);
          }
        });
        
        log.info('[EntityStore] ✅ Entity event listener registered successfully');
      } catch (error) {
        log.error('[EntityStore] ❌ Failed to setup entity event listener:', error);
      }

      // Bail if a newer initialize() call has taken over for a different room key
      if (!isStillCurrent()) {
        log.warn('[EntityStore] Room key changed during initialization, discarding stale result for:', initForRoomKey);
        cleanupCRDT();
        if (cleanupEntityEvents) cleanupEntityEvents();
        return;
      }

      // ✅ Update computed state
      const connectedIntegrations = get()._updateConnectedIntegrations(entities);
      
      set({
        entities: buildEntityHierarchy(entities),
        connectedIntegrations,
        status: 'idle',
        lifecycle: 'ready',
        isObserving: true,
        cleanupFn: () => {
          cleanupCRDT();
          if (cleanupEntityEvents) {
            cleanupEntityEvents();
          }
          set({ isObserving: false });
        },
        dataspaceRoomKey: initForRoomKey,
        initialized: true,
      });
      
      const totalTime = performance.now() - perfStart;
      log.info(`📊 [PERF SUMMARY] REST fetch - Total time: ${totalTime.toFixed(2)}ms`);
      log.info(`⏱️ [PERF] EntityStore initialization COMPLETE: ${totalTime.toFixed(2)}ms total`);

      // Schedule throttled orphan IDB cleanup (runs at most once per 24h, 60s delay).
      import('../services/idb/idbCleanup').then(({ scheduleOrphanCleanup }) => {
        const ids = new Set(entities.map((e: any) => e._id || e.id).filter(Boolean));
        scheduleOrphanCleanup(ids);
      }).catch(() => {});

      // Sync fresh entities with window entities (deferred to avoid circular imports).
      setTimeout(async () => {
        // Guard: abort if a newer initialize() has taken over for a different space.
        if (!isStillCurrent()) {
          log.warn('[EntityStore] Space changed before window sync — discarding');
          return;
        }
        try {
          const { useSpaceStore } = await import('./spaceStore');
          const spaceStore = useSpaceStore.getState();

          log.info('[EntityStore] ✅ Syncing fresh entities with window entities');

          let syncCount = 0;
          Object.entries(spaceStore.windows).forEach(([windowId, windowEntity]) => {
            if (windowEntity.entityId) {
              const freshEntity = entities.find((e: any) => e._id === windowEntity.entityId);
              // Sync window title when entity name changes; entity data lives in entityStore
              if (freshEntity && windowEntity.title !== freshEntity.name) {
                spaceStore.updateWindow(windowId, { title: freshEntity.name });
                syncCount++;
              }
            }
          });

          log.info('[EntityStore] 🎯 Entity sync complete:', {
            totalWindows: Object.keys(spaceStore.windows).length,
            entitiesUpdated: syncCount
          });
        } catch (error) {
          log.error('[EntityStore] Failed to sync entities with windows:', error);
        }
      }, 0);
    } catch (error) {
      log.error('[EntityStore] Failed to initialize:', error);
      
      // Check if it's an authentication error
      if (error instanceof Error && (error.message.includes('authenticated') || error.message.includes('401'))) {
        log.warn('[EntityStore] Authentication error during initialization - will retry when auth is available');
        set({ 
          status: 'idle',
          lifecycle: 'uninitialized',  // ✅ NEW: Reset lifecycle on auth error
          initialized: false,
          error: undefined,
          cleanupFn: undefined,
          initializationPromise: null,  // ✅ Clear promise on auth error
        });
        // Don't throw on auth errors - let SpaceStore handle gracefully
        return;
      } else {
        if (get().initializationEpoch === epoch) {
          set({
            status: 'error',
            lifecycle: 'error',
            error: error as Error,
            initialized: false,
            cleanupFn: undefined,
            initializationPromise: null,
          });
        }
        throw error;
      }
    } finally {
      // Only clear the promise if we're still the active init — a newer
      // initialize() call may have already replaced it.
      if (get().initializationEpoch === epoch) {
        set({ initializationPromise: null });
      }
    }
    })();

    // Store the promise so subsequent calls can wait for it
    set({ initializationPromise: initPromise });

    // Return the promise so callers can await it
    return initPromise;
  },

  addEntity: (entity: BaseEntityType, _parentId?: string) => {
    // Thin wrapper around _applyLocalUpsert for backward compatibility
    _applyLocalUpsert(entity, {
      log: true,
      source: 'local',
      syncCRDT: true
    });
  },

  deleteEntity: (id) => {
    // Thin wrapper around _applyLocalDelete for backward compatibility
    _applyLocalDelete(id, {
      log: true,
      source: 'local',
      syncCRDT: true
    });
  },

  updateEntity: (entity) => {
    // Thin wrapper around _applyLocalUpsert for backward compatibility
    _applyLocalUpsert(entity, {
      log: true,
      source: 'local',
      syncCRDT: true
    });
  },

  removeEntity: (entityId: string) => {
    set((state) => {
      // Helper to remove entity from tree
      const removeFromTree = (entities: BaseEntityType[]): BaseEntityType[] => {
        return entities.filter((e) => {
          if (e._id === entityId) return false;
          if (e.children) {
            e.children = removeFromTree(e.children);
          }
          return true;
        });
      };

      return {
        entities: removeFromTree(state.entities),
      };
    });
  },

  cleanup: () => {
    const state = get();
    
    // entity-system Y.Doc observer cleanup is handled by the cleanupFn below.
    // (entityCRDTService.cleanup removed — entityCRDTService no longer used here.)
    
    // Cleanup other resources
    if (state.cleanupFn) {
      state.cleanupFn();
      set({
        initialized: false,
        cleanupFn: undefined,
        isObserving: false,
        dataspaceRoomKey: undefined,
      });
    }
    
    // ✅ PHASE 2: No hierarchyStore to reset - filtering is done via selectors
    if (state.dataspaceRoomKey) {
      log.info('[EntityStore] Dataspace cleanup - filtering ready');
    }
    
    // ✅ BUG FIX #3: Clear memoization caches on cleanup
    // Prevents memory leaks and stale data when switching spaces or logging out
    cachedDisplayableEntities = [];
    lastEntitiesRef = null;
    cachedTrashedEntities = [];
    lastTrashedRef = null;
    log.info('[EntityStore] ✅ Memoization caches cleared');
    
    // Clear any remaining entity cache entries (all keys, all dataspaces)
    entityCacheService.clear().catch(error => {
      log.error('[EntityStore] Failed to clear cache on cleanup:', error);
    });
  },

  setAuthToken: (token: string | null) => {
    if (token) {
      localStorage.setItem('authToken', token);
    } else {
      localStorage.removeItem('authToken');
    }
    set({ authToken: token });
  },

  setEntities: (entities: BaseEntityType[]) => {
    // ⚠️ WARNING: This bypasses the mutation funnel and should be avoided
    const connectedIntegrations = get()._updateConnectedIntegrations(entities);
    set({ 
      entities: buildEntityHierarchy(entities),
      connectedIntegrations
    });
  },

  fetchEntities: async () => {
    set({ status: 'loading' });

    try {
      const dataspaceRoomKey = get().dataspaceRoomKey;
      if (!dataspaceRoomKey) {
        throw new Error('No dataspaceRoomKey set');
      }

      console.log('[EntityStore] 📡 fetchEntities called:', JSON.stringify({
        dataspaceRoomKey,
        beforeFetch_entityCount: get().entities.length
      }, null, 2));

      // Don't pass 'default' as parentId - it should be undefined for the default space
      const parentId = dataspaceRoomKey === 'default' ? undefined : dataspaceRoomKey;
      const fetchedEntities = await fileService.getList(parentId);

      console.log('[EntityStore] 📥 Fetched entities from API:', JSON.stringify({
        parentId,
        fetchedCount: fetchedEntities.length,
        entityTypes: Array.from(new Set(fetchedEntities.map((e: any) => e.entityType))),
        entityTypeCounts: fetchedEntities.reduce((acc: any, e: any) => {
          acc[e.entityType] = (acc[e.entityType] || 0) + 1;
          return acc;
        }, {}),
        sampleEntities: fetchedEntities.slice(0, 5).map((e: any) => ({
          _id: e._id,
          llmId: e.llmId,
          name: e.name,
          entityType: e.entityType,
          spaceId: e.spaceId
        }))
      }, null, 2));

      // ✅ OPTIMIZATION: Don't pre-populate CRDT with all entities to avoid WebSocket payload size issues
      // CRDT should only track incremental updates (create/update/delete), not full entity lists

      // Normalize entities (strip large content blobs, etc.) and update
      // connectedIntegrations — same pipeline as the primary initialize() path.
      const normalizedEntities = fetchedEntities.map(normalizeEntityForCache);
      const connectedIntegrations = get()._updateConnectedIntegrations(normalizedEntities);
      set({
        entities: buildEntityHierarchy(normalizedEntities),
        connectedIntegrations,
        status: 'idle',
      });
      
      console.log('[EntityStore] ✅ Entities set in store:', JSON.stringify({
        totalEntities: get().entities.length,
        entityTypes: Array.from(new Set(get().entities.map((e: any) => e.entityType)))
      }, null, 2));
    } catch (error) {
      console.error('[EntityStore] ❌ fetchEntities failed:', JSON.stringify({
        error: error instanceof Error ? error.message : String(error),
        dataspaceRoomKey: get().dataspaceRoomKey
      }, null, 2));
      set({ status: 'error', error: error as Error });
      throw error;
    }
  },

  performDelete: async (id: string) => {
    try {
      // Immediately update local state for responsive UI
      get().deleteEntity(id);

      // Then try backend and CRDT updates in parallel
      try {
        // REST delete broadcasts to entity-system Y.Doc; no separate CRDT call needed.
        const [backendResult] = await Promise.all([entityService.deleteEntity(id)]);

        if (!backendResult) {
          // Entity might already be deleted
        }
      } catch (error) {
        // Fetch fresh entities to restore state
        await get().fetchEntities();
        throw error;
      }
    } catch (error) {
      throw error;
    }
  },

  logEvent: (eventData) => {
    const event: EntityEvent = {
      ...eventData,
      id: uuidv4(),
      timestamp: Date.now(),
    };

    // Cap undo/redo/event stacks to prevent unbounded memory growth in long sessions.
    const MAX_STACK = 100;
    set((state) => ({
      events: [...state.events, event].slice(-MAX_STACK),
      undoStack: [...state.undoStack, event].slice(-MAX_STACK),
      redoStack: [], // Clear redo stack when new event occurs
    }));
  },

  undo: () => {
    const state = get();
    const lastEvent = state.undoStack[state.undoStack.length - 1];

    if (!lastEvent) return;

    // Remove from undo stack and add to redo stack
    set((state) => ({
      undoStack: state.undoStack.slice(0, -1),
      redoStack: [...state.redoStack, lastEvent],
    }));

    // Reverse the action using helpers (log: false to avoid adding undo action during undo)
    switch (lastEvent.action) {
      case 'create':
        _applyLocalDelete(lastEvent.entityId, {
          log: false,
          source: 'local',
          syncCRDT: true
        });
        break;
      case 'update':
        if (lastEvent.previousData) {
          _applyLocalUpsert(lastEvent.previousData, {
            log: false,
            source: 'local',
            syncCRDT: true
          });
        }
        break;
      case 'delete':
        if (lastEvent.previousData) {
          // ✅ CRITICAL: Ensure entity is not marked as trashed when restoring
          const restoredEntity = { 
            ...lastEvent.previousData, 
            trashed: false 
          };
          
          _applyLocalUpsert(restoredEntity, {
            log: false,
            source: 'local',
            syncCRDT: true
          });
          
          // ✅ CRITICAL: If restoring a Group, also restore its children's groupIds
          if (restoredEntity.entityType === 'Group') {
            const childEntityIds = restoredEntity.skeleton?.childEntities || [];
            
            // Update each child to point back to this folder
            for (const childId of childEntityIds) {
              const child = get().entities.find(e => e._id === childId);
              if (child) {
                _applyLocalUpsert({
                  ...child,
                  skeleton: {
                    ...child.skeleton,
                    groupIds: [lastEvent.entityId]
                  }
                }, {
                  log: false,
                  source: 'local',
                  syncCRDT: true
                });
              }
            }
          }
        }
        break;
    }
  },

  redo: () => {
    const state = get();
    const lastRedoEvent = state.redoStack[state.redoStack.length - 1];

    if (!lastRedoEvent) return;

    // Remove from redo stack and add to undo stack
    set((state) => ({
      redoStack: state.redoStack.slice(0, -1),
      undoStack: [...state.undoStack, lastRedoEvent],
    }));

    // Replay the action using helpers (log: false to avoid adding undo action during redo)
    switch (lastRedoEvent.action) {
      case 'create':
        if (lastRedoEvent.data) {
          _applyLocalUpsert(lastRedoEvent.data, {
            log: false,
            source: 'local',
            syncCRDT: true
          });
        }
        break;
      case 'update':
        if (lastRedoEvent.data) {
          _applyLocalUpsert(lastRedoEvent.data, {
            log: false,
            source: 'local',
            syncCRDT: true
          });
        }
        break;
      case 'delete':
        _applyLocalDelete(lastRedoEvent.entityId, {
          log: false,
          source: 'local',
          syncCRDT: true
        });
        break;
    }
  },

  // Debounce the space update handler
  onSpaceUpdate: debounce((spaceId: string, update: SpaceUpdate) => {
    if (update.windows) {
      // ✅ FIX: Use helper instead of direct state mutation
      const spaceEntity = get().entities.find((e) => e.entityType === 'Space' && e._id === spaceId);
      
      if (spaceEntity) {
        _applyLocalUpsert({
          ...spaceEntity,
          skeleton: {
            ...spaceEntity.skeleton,
            windows: update.windows,
          },
        }, {
          source: 'local',
          syncCRDT: false, // Don't sync back (this came from space updates)
          log: false
        });
      }
    }
  }, 100),
  
  getEntityById: (id: string) => {
    return get().entities.find(e => e._id === id || e.id === id);
  },

  getEntityByLlmId: (id: string) => {
    return get().entities.find(e => e.llmId === id || e._id === id);
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 🔍 ENTITY FILTERING SELECTORS (Phase 2 Cleanup - Replaces HierarchyStore)
  // ═══════════════════════════════════════════════════════════════════════════
  
  /**
   * Get displayable entities (filtered by type and trash status)
   * Filters out system entities that shouldn't appear in the sidebar
   */
  getDisplayableEntities: () => {
    const state = get();
    const entities = state.entities;
    
    // ✅ BUG FIX #1: Return cached result if entities reference hasn't changed
    // Changed: 'cachedDisplayableEntities.length > 0' → 'lastEntitiesRef !== null'
    // Reason: The old condition failed when result was legitimately empty (no files)
    // This caused infinite re-renders when sidebar was empty
    if (entities === lastEntitiesRef && lastEntitiesRef !== null) {
      return cachedDisplayableEntities;
    }
    
    // Filter to displayable entities only
    const displayableEntities = entities.filter((entity) => {
      // Filter out trashed entities
      if ((entity as any).trashed === true) {
        return false;
      }
      
      // ⚡ OPTIMIZED: Use Set.has() for O(1) lookup instead of multiple if checks
      if (HIDDEN_ENTITY_TYPES.has(entity.entityType) || (entity.skeleton as any)?.isDuplicate) {
        return false;
      }
      
      // Filter out all Integration sub-types (Integration.Gmail, Integration.GitHub, etc.)
      // mailAppStore reads entityStore.entities directly so this doesn't affect its lookups.
      if (entity.entityType?.startsWith('Integration.')) {
        return false;
      }

      // Filter out LocalFile only (GoogleDrive files are now user-visible in sidebar)
      if (entity.entityType === 'LocalFile' ||
          entity.entityType?.startsWith('External.LocalFile')) {
        return false;
      }
      
      // Filter out embedded entities and explicitly hidden entities
      const skeleton = (entity.skeleton as any) || {};
      if (skeleton.isEmbedded === true || skeleton.embedded === true || skeleton.parentEntityId) {
        return false;
      }
      if ((entity as any).hidden === true || skeleton.hidden === true) {
        return false;
      }
      
      // Filter pasted images by naming pattern
      if (entity.entityType?.startsWith('File.Image')) {
        const isPastedImage = entity.name?.match(/^pasted-image-\d+\.(png|jpg|jpeg|gif|webp)$/i);
        if (isPastedImage) {
          return false;
        }
      }
      
      return true;
    });
    
    // Create synthetic system groups if needed
    
    // Add Chats group if there are orphaned threads.
    // Orphaned threads are moved *into* the synthetic group and removed from the
    // flat list so they don't appear twice (once at root, once under Chats).
    const threads = displayableEntities.filter(e => e.entityType === 'Thread');
    const orphanedThreads = threads.filter(t => {
      const groupIds = (t.skeleton as any)?.groupIds || [];
      return groupIds.length === 0;
    });
    const orphanedThreadIds = new Set(orphanedThreads.map(t => t._id));
    
    // Exclude orphaned threads from the flat list — they belong inside the Chats group
    const result = displayableEntities.filter(e => !orphanedThreadIds.has(e._id));
    
    if (orphanedThreads.length > 0) {
      const spaceChatEntity: BaseEntityType = {
        _id: SYSTEM_ENTITY_IDS.SPACECHAT,
        entityType: 'Group',
        name: 'Chats',
        skeleton: {
          '@type': 'Group',
          groupName: 'Chats',
          childEntities: orphanedThreads.map(t => t._id),
          isSystemGroup: true,
        } as any,
        children: orphanedThreads,
        created_at: STABLE_SYSTEM_TIMESTAMP,
        updated_at: STABLE_SYSTEM_TIMESTAMP,
      } as BaseEntityType;
      
      result.unshift(spaceChatEntity);
    }
    
    // ✅ Update cache to prevent infinite loops
    lastEntitiesRef = entities;
    cachedDisplayableEntities = result;
    
    return result;
  },
  
  /**
   * Get all trashed entities
   * ✅ Memoized to prevent infinite re-render loops
   */
  getTrashedEntities: () => {
    const state = get();
    const entities = state.entities;
    
    // ✅ Memoization: Return cached result if entities reference hasn't changed
    if (entities === lastTrashedRef && lastTrashedRef !== null) {
      return cachedTrashedEntities;
    }
    
    const trashed = entities.filter(entity => {
      if ((entity as any).trashed !== true) {
        return false;
      }
      
      if ((entity as any).hidden === true) {
        return false;
      }
      
      // ⚡ OPTIMIZED: Use Set.has() for O(1) lookup
      if (HIDDEN_ENTITY_TYPES.has(entity.entityType) || (entity.skeleton as any)?.isDuplicate) {
        return false;
      }
      
      // Also hide hierarchical Integration types (Integration.Gmail, Integration.Calendar, etc.)
      if (entity.entityType?.startsWith('Integration.')) {
        return false;
      }
      
      return true;
    });
    
    // ✅ Update cache to prevent infinite loops
    lastTrashedRef = entities;
    cachedTrashedEntities = trashed;
    
    return trashed;
  },
  
  /**
   * Get synthetic system groups (Chats)
   */
  getSystemGroups: () => {
    const displayableEntities = get().getDisplayableEntities();
    const spaceChatGroup = displayableEntities.find(e => e._id === SYSTEM_ENTITY_IDS.SPACECHAT) || null;
    return { spaceChatGroup };
  },
};
});

const buildEntityHierarchy = (entities: BaseEntityType[]): BaseEntityType[] => {
  // ✅ PERFORMANCE FIX: Return ALL entities as flat list
  // Components will build their own hierarchies as needed using buildEntityBrowserApp2Hierarchy
  // This avoids double-processing and allows different components to use different hierarchy views
  return entities;
};

export const getEntityStore = () => {
  const entityStore = useEntityStore.getState();
  return entityStore;
}

export default useEntityStore;

/**
 * System entity IDs for synthetic groups
 * Re-exported for compatibility with files that previously imported from hierarchyStore
 */
export const SYSTEM_ENTITY_IDS = {
  SPACECHAT: '__spacechat__',
  INTEGRATIONS: '__integrations__',
} as const;

// ═══════════════════════════════════════════════════════════════════════════
// 🔄 AUTOMATIC INDEXEDDB SYNC VIA ZUSTAND SUBSCRIPTION
// ═══════════════════════════════════════════════════════════════════════════
//
// ARCHITECTURE:
// - Subscribes to ALL entity changes (create, update, delete, CRDT, events)
// - Automatically syncs to IndexedDB when entities reference changes
// - 100% coverage - impossible to miss a mutation point
// - Single source of truth for cache sync logic
//
// GUARDS:
// 1. Reference equality - only syncs when entities array reference changes
// 2. Lifecycle check - skips during 'loading' and 'uninitialized' states
// 3. UserId check - must have authenticated user
//
// BENEFITS:
// - Robust: Catches all 12+ mutation points automatically
// - Maintainable: No need to remember manual cache calls
// - Clean: Centralized sync logic in one place
// - Safe: Guards prevent syncing during initial load
//
// ═══════════════════════════════════════════════════════════════════════════

// entityStore no longer maintains its own IndexedDB cache.
// The entity-system Y.Doc's y-indexeddb (managed by entitySystemYjsStore) is the
// correct caching layer — it is automatically kept in sync by Y.js while the client
// is connected, and _seedEntitiesFromMap reconciles entityStore from it on startup.
// A separate entityStore cache only introduced stale-data bugs.

// ─── Performance canary: detect entity array resets ──────────────────────────
// Finding #2: the systemStore init effect resets entities=[] when the dataspace
// key mismatches. After fixing finding #2, entityStoreResetCount should stay ≤ 1.
// This subscription is no-op in production (perf.increment is a no-op when IS_PROD).
import('../utils/perfInstrumentation').then(({ perf }) => {
  let prevLength = useEntityStore.getState().entities.length;
  useEntityStore.subscribe((state) => {
    const len = state.entities.length;
    if (len === 0 && prevLength > 0) {
      perf.increment('entityStoreResetCount');
    }
    prevLength = len;
  });
}).catch(() => {});
